/**
 * Player.tsx
 * =============================================================================
 * Main player component for Neuro-Audio Lab.
 * Features mode selection, transport controls, timer, and mixer faders.
 *
 * @author Neuro-Audio Lab
 * @version 3.0.0 - Two-Column Layout (Zero-Scroll Dashboard)
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { APP_NAME, APP_VERSION, APP_STUDIO, APP_COUNTRY, APP_YEAR } from '../../config/constants';
import { Link } from 'react-router-dom';
import {
  Play,
  Pause,
  Square,
  Waves,
  Mic,
  Music,
  Volume,
  SlidersHorizontal,
  ChevronUp,
  ChevronDown,
  FlaskConical,
  MicOff,
} from 'lucide-react';
import { useAudioEngine, useTimer } from '../../hooks/useAudioEngine';
import { usePlayerStore } from '../../store/playerStore';
import { TrackFader } from './TrackFader';
import { PlayerSidebar } from './PlayerSidebar';
import Spectrogram from './Spectrogram';
import type { AudioMode } from '../../core/AudioEngine';
import { getAudioEngine } from '../../core/AudioEngine';
import type { TimerProfileId } from '../../core/types';
import { MODE_CONFIGS, getDefaultAmbience, getDefaultVoice, getEntrainmentUrl, getTriggerUrl } from '../../config/audioPresets';
import { TIMER_PROFILES } from '../../core/types';

// =============================================================================
// CONSTANTS
// =============================================================================

const AVAILABLE_MODES: AudioMode[] = ['focus', 'sleep', 'anxiety'];

const MODE_PHRASES: Record<AudioMode, string> = {
  focus: 'Estado de Concentración Profunda',
  sleep: 'Descanso Reparador',
  anxiety: 'Calma Interior',
};

// =============================================================================
// PLAYER PROPS
// =============================================================================

interface PlayerProps {
  initialMode?: AudioMode;
}

// =============================================================================
// MAIN PLAYER COMPONENT
// =============================================================================

export function Player({ initialMode }: PlayerProps) {
  // ===========================================================================
  // HOOKS (PROTECTED - DO NOT MODIFY)
  // ===========================================================================
  const {
    isInitialized,
    isPlaying,
    isPaused,
    isLoading,
    currentMode,
    volumes,
    masterVolume,
    initialize,
    play,
    pause,
    resume,
    stop,
    setMode,
    setVolume,
    setMasterVolume,
    loadAmbience,
    clearAmbience,
    loadVoice,
    loadEntrainment,
    clearEntrainment,
    loadTrigger,
    clearTrigger,
  } = useAudioEngine();

  const {
    timerState,
    currentTimerProfile,
    sessionElapsedTime,
    remainingTime,
    formattedSession,
    formattedRemaining,
    pomodoroPhase,
    setTimerProfile,
    startTimer,
    pauseTimer,
    resetTimer,
  } = useTimer();

  // ===========================================================================
  // LOCAL STATE
  // ===========================================================================

  // [v3.4] Voice guide toggle state (persisted)
  const isVoiceEnabled = usePlayerStore((s) => s.isVoiceEnabled);
  const setVoiceEnabled = usePlayerStore((s) => s.setVoiceEnabled);

  // Mixer visibility state (default: closed for anxiety-friendly UI)
  const [showMixer, setShowMixer] = useState(false);

  // [v3.3] Ref to guard auto-load effect against redundant re-fetches on remount
  const isFirstAutoLoadRef = useRef(true);

  // ===========================================================================
  // EFFECTS (PROTECTED - DO NOT MODIFY)
  // ===========================================================================

  // [v3.3] Effect to set the audio mode on mount.
  //
  // GUARD: If a session is already active (playing or paused), do NOT
  // overwrite the current mode on remount. This prevents the bug where
  // navigating to /science and back resets the UI to Focus mode and
  // fires duplicate audio loads (audio overlap + volume corruption).
  //
  // We also preserve the last selected mode (even when stopped) so the
  // user's selection survives navigation. Only on a truly fresh session
  // (null mode after hard refresh) do we apply the smart default.
  //
  // getState() reads a snapshot without subscribing, so isPlaying and
  // isPaused are NOT in the dependency array (adding them would re-run
  // this effect on every play/pause transition — not what we want).
  useEffect(() => {
    const { isPlaying: active, isPaused: paused, currentMode: activeMode } = usePlayerStore.getState();

    // If audio is running or paused, the session owns the mode — hands off
    if (active || paused) {
      return;
    }

    // If a mode was already selected (stopped but not cleared), keep it
    if (activeMode) {
      return;
    }

    // Fresh session (null mode on hard refresh) — apply smart default
    const targetMode = initialMode || 'focus';
    setMode(targetMode);
  }, [initialMode, setMode]);

  // CRITICAL: Hydrate timer on mount if it was saved as 0 in localStorage
  // This ensures countdown timers (ultradian, sleep_timer, pomodoro) show their
  // correct duration immediately when entering the app directly
  useEffect(() => {
    // Only run once on mount
    if (remainingTime === 0 && currentTimerProfile !== 'free') {
      // Force reload of current profile to hydrate the duration
      setTimerProfile(currentTimerProfile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = run once on mount

  // Auto-load default tracks when mode changes.
  //
  // [v3.1] HYDRATION FIX: We now REQUIRE currentMode to be explicitly set
  // before loading any tracks. The old code used `currentMode || 'focus'`
  // which caused a race condition on hard refresh:
  //
  //   FIRST RENDER:  currentMode = null (Zustand hasn't hydrated)
  //   Effect 1:      setMode('sleep') → playId becomes 1
  //   Effect 2:      sees currentMode=null → loads FOCUS with playId=1 ← BUG!
  //   RE-RENDER:     currentMode = 'sleep'
  //   Effect 2:      loads SLEEP with playId=1 (same playId!)
  //   RESULT:        Both Focus and Sleep race with identical playId.
  //                  Whichever fetch finishes LAST overwrites the buffer.
  //
  // The fix: bail out if currentMode is null. The initialMode effect above
  // guarantees setMode() fires on mount, which triggers a re-render with
  // a valid currentMode. Only THEN does this effect load the correct tracks.
  useEffect(() => {
    if (!isInitialized) return;

    // [v3.1] GUARD: Do NOT load tracks until a mode is explicitly set.
    // This prevents the hydration race where null falls back to 'focus'
    // and competes with the real mode's loads under the same playId.
    if (!currentMode) {
      return;
    }

    // [v3.3] GUARD: On first mount during an active session, skip reloading.
    // Tracks are already loaded and sourceNodes are running from before
    // navigation. Reloading would waste bandwidth and re-trigger the
    // Pavlovian bell (one-shot that already finished playing naturally).
    // Subsequent runs (user-initiated mode changes) always proceed.
    if (isFirstAutoLoadRef.current) {
      isFirstAutoLoadRef.current = false;
      const { isPlaying: active, isPaused: paused } = usePlayerStore.getState();
      if (active || paused) {
        return;
      }
    }

    const mode = currentMode;

    // Load default ambience track for the current mode.
    // [v3.0] If this mode has no ambience, explicitly clear the stale buffer
    // from the previous mode so startAmbienceSource() doesn't replay it.
    const ambienceTrack = getDefaultAmbience(mode);
    if (ambienceTrack) {
      loadAmbience(ambienceTrack.url).catch((err) => {
        console.error('[Player] Failed to load ambience:', err);
      });
    } else {
      clearAmbience();
    }

    // [v2.8] Load entrainment track if the mode has one, otherwise clear it
    const entrainmentUrl = getEntrainmentUrl(mode);
    if (entrainmentUrl) {
      loadEntrainment(entrainmentUrl).catch((err) => {
        console.error('[Player] Failed to load entrainment:', err);
      });
    } else {
      clearEntrainment();
    }

    // [v2.9] Load one-shot trigger if the mode has one, otherwise clear it
    const triggerUrl = getTriggerUrl(mode);
    if (triggerUrl) {
      loadTrigger(triggerUrl).catch((err) => {
        console.error('[Player] Failed to load trigger:', err);
      });
    } else {
      clearTrigger();
    }

    // Load default voice guide if available (mainly for anxiety mode)
    const voiceTrack = getDefaultVoice(mode);
    if (voiceTrack) {
      loadVoice(voiceTrack.url).catch((err) => {
        console.error('[Player] Failed to load voice:', err);
      });
    }
  }, [currentMode, isInitialized, loadAmbience, clearAmbience, loadVoice, loadEntrainment, clearEntrainment, loadTrigger, clearTrigger]);

  // Smart Defaults: Auto-select timer profile based on audio mode
  // Force reset when mode changes to allow profile selection
  useEffect(() => {
    const mode = currentMode || 'focus';

    // Select appropriate profile for the new mode (Scientific defaults)
    // Focus -> Ultradian (90 min BRAC cycle)
    // Sleep -> Sleep Timer (45 min)
    // Anxiety -> Reset Timer (15 min regulación del sistema nervioso)
    let suggestedProfile: TimerProfileId;

    switch (mode) {
      case 'focus':
        suggestedProfile = 'ultradian'; // 90 min work blocks (BRAC cycle)
        break;
      case 'sleep':
        suggestedProfile = 'sleep_timer'; // 45 min default
        break;
      case 'anxiety':
        suggestedProfile = 'reset_timer'; // 15 min reset (regulación del sistema nervioso)
        break;
      default:
        suggestedProfile = 'free';
    }

    // Always change profile when mode changes
    if (suggestedProfile !== currentTimerProfile) {
      setTimerProfile(suggestedProfile);
      // CRITICAL: Reset immediately after changing profile to load new duration
      resetTimer();
    }
  }, [currentMode]); // Removed other dependencies to avoid infinite loops

  // Synchronize Audio Playback with Timer State
  useEffect(() => {
    // Play -> Start timer
    if (isPlaying && !isPaused && timerState !== 'running') {
      startTimer();
    }

    // Pause -> Pause timer
    if (isPaused && timerState === 'running') {
      pauseTimer();
    }

    // Stop (idle) -> Pause timer (keep session data)
    if (!isPlaying && !isPaused && timerState === 'running') {
      pauseTimer();
    }
  }, [isPlaying, isPaused, timerState, startTimer, pauseTimer]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent spacebar from triggering if a button has focus
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();

        // [v2.6] MOBILE FIX: Unlock audio on spacebar (same as click)
        getAudioEngine().ensureContext();

        // Blur any focused button to prevent double-trigger
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        // Inline logic to avoid dependency issues
        if (!isInitialized) {
          initialize().then(() => {
            const mode = currentMode || 'focus';
            play(mode);
            startTimer();
          });
        } else if (isPlaying && !isPaused) {
          pause();
          pauseTimer();
        } else if (isPaused) {
          resume();
          startTimer();
        } else {
          const mode = currentMode || 'focus';
          play(mode);
          startTimer();
        }
      }
      if (e.code === 'Escape') {
        e.preventDefault();
        stop();
        resetTimer();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isInitialized, isPlaying, isPaused, currentMode, initialize, play, pause, resume, stop, startTimer, pauseTimer, resetTimer]);

  // ===========================================================================
  // DERIVED STATE (PROTECTED - DO NOT MODIFY)
  // ===========================================================================

  const modeConfig = currentMode ? MODE_CONFIGS[currentMode] : MODE_CONFIGS.focus;
  const isStopDisabled = !isPlaying && !isPaused;
  const activeModeId = currentMode || 'focus';

  // ===========================================================================
  // HANDLERS (PROTECTED - DO NOT MODIFY)
  // ===========================================================================

  const handleModeSelect = useCallback(
    async (mode: AudioMode) => {
      // [v3.1] setMode() increments the generation ID (playId), which
      // invalidates all in-flight loads for the previous mode. The auto-load
      // effect will fire and start loading the new mode's tracks.
      setMode(mode);

      // [v3.1] FIX: If audio was playing or paused, do a clean stop→play
      // transition. Previously this used setTimeout(100ms) which was a race
      // condition — the play() could fire before stop() finished, causing
      // zombie nodes. Now we await stop() through the operation queue,
      // guaranteeing it completes before play() begins.
      if (isPlaying || isPaused) {
        await stop();
        play(mode);
      }
    },
    [setMode, isPlaying, isPaused, stop, play]
  );

  const handlePlayPause = useCallback(() => {
    // [v2.6] MOBILE FIX: "Silent Unlocker" pattern for iOS Safari
    // This MUST be the first line - before any async operations or state checks
    // Plays a ghost sound (0.001s silence) to wake up the audio hardware
    getAudioEngine().ensureContext();

    if (!isInitialized) {
      initialize().then(() => {
        const mode = currentMode || 'focus';
        play(mode);
        startTimer();
      });
    } else if (isPlaying && !isPaused) {
      pause();
      pauseTimer();
    } else if (isPaused) {
      resume();
      startTimer();
    } else {
      const mode = currentMode || 'focus';
      play(mode);
      startTimer();
    }
  }, [
    isInitialized,
    isPlaying,
    isPaused,
    currentMode,
    initialize,
    play,
    pause,
    resume,
    startTimer,
    pauseTimer,
  ]);

  const handleStop = useCallback(() => {
    stop();
    resetTimer();
  }, [stop, resetTimer]);

  const handleSelectTimerProfile = useCallback(
    (profileId: TimerProfileId) => {
      // Switch the timer profile without stopping audio.
      // resetTimer() sets timerState to 'idle', which combined with
      // the sync effect (line ~286) would cause a race condition.
      // Instead, just set the new profile — the sync effect will
      // handle restarting the timer if audio is still playing.
      setTimerProfile(profileId);
    },
    [setTimerProfile]
  );

  const handleVolumeChange = useCallback(
    (trackId: 'ambience' | 'tone' | 'voice' | 'master', volume: number) => {
      if (trackId === 'master') {
        setMasterVolume(volume);
      } else {
        setVolume(trackId, volume);
      }
    },
    [setVolume, setMasterVolume]
  );

  // ===========================================================================
  // RENDER: Two-Column Layout (Zero-Scroll Dashboard)
  // ===========================================================================

  return (
    <div className="flex flex-col md:flex-row fixed inset-0 w-full h-dvh bg-neutral-950 overflow-hidden m-0 p-0">
      {/* ================================================================== */}
      {/* COLUMN 1: Sidebar (Mode Selection + Timer Toggle) */}
      {/* Mobile: Top bar | Desktop: Left sidebar */}
      {/* ================================================================== */}
      <PlayerSidebar
        modes={AVAILABLE_MODES}
        currentModeId={activeModeId}
        onSelectMode={(id) => handleModeSelect(id as AudioMode)}
        currentTimerProfile={currentTimerProfile}
        onSelectTimerProfile={handleSelectTimerProfile}
      />

      {/* ================================================================== */}
      {/* COLUMN 2: Main Stage */}
      {/* ================================================================== */}
      <main className="flex-1 flex flex-col relative overflow-y-auto md:overflow-y-auto overflow-hidden">
        {/* Atmospheric Background Gradient */}
        <div
          className="absolute inset-0 pointer-events-none transition-all duration-1000"
          style={{
            background: `radial-gradient(ellipse 80% 50% at 50% 50%, ${modeConfig.color}08 0%, transparent 70%)`,
          }}
        />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col items-stretch justify-start md:justify-center relative z-10 px-4 md:px-8 py-6 md:py-4">
          {/* Header: Mode Title + Inspirational Phrase - Hidden on mobile (shown in sidebar) */}
          <header className="hidden md:block text-center mb-4 mt-4 mx-auto">
            <h1
              className="text-3xl font-light tracking-tight mb-2 transition-colors duration-500"
              style={{ color: modeConfig.color }}
            >
              {modeConfig.name}
            </h1>
            <p className="text-lg text-neutral-400/60 font-light tracking-wide">
              {MODE_PHRASES[activeModeId]}
            </p>
          </header>

          {/* Central Container - Mobile: relative anchor for ghost wave, Desktop: normal flow */}
          <div className="relative flex flex-col items-center justify-start mx-auto w-full max-w-3xl min-h-[320px] md:min-h-0 md:justify-center md:items-stretch">
            {/* Wave Layer - Mobile: Ghost Background (absolute), Desktop: Normal Block (relative) */}
            <div className="absolute inset-0 z-0 opacity-30 pointer-events-none flex items-center justify-center md:relative md:inset-auto md:w-full md:max-w-3xl md:h-40 md:mb-6 md:mx-auto md:opacity-100 md:pointer-events-auto">
              <Spectrogram
                isActive={isPlaying && !isPaused}
                modeId={activeModeId}
                color={modeConfig.color}
              />
            </div>

            {/* Timer Display (Digital Display - Always Visible) */}
            <div className="relative z-10 mt-2 mb-10 md:mt-0 md:mb-6 mx-auto">
              <div className="flex flex-col md:flex-row items-center gap-2 md:gap-2 px-6 md:px-6 py-3 md:py-2 rounded-2xl md:rounded-full bg-black/20 backdrop-blur-sm border border-white/5">
                <span className="text-[10px] md:text-[10px] text-neutral-500 uppercase tracking-widest font-semibold">
                  {currentTimerProfile === 'free' ? 'Sesión' : TIMER_PROFILES[currentTimerProfile].name}
                </span>
                <span className="text-5xl md:text-3xl font-mono font-bold text-neutral-300 tabular-nums tracking-tight">
                  {currentTimerProfile === 'free' ? formattedSession : formattedRemaining}
                </span>
                {currentTimerProfile !== 'free' && sessionElapsedTime > 0 && (
                  <span className="text-[9px] md:text-[10px] text-neutral-600 font-mono ml-0.5 md:ml-1 hidden md:inline">
                    ({formattedSession})
                  </span>
                )}
              </div>
              {/* Pomodoro phase indicator */}
              {pomodoroPhase !== 'none' && timerState === 'running' && (
                <div className="flex justify-center mt-2">
                  <span
                    className={`text-[10px] uppercase tracking-widest font-semibold px-3 py-0.5 rounded-full ${
                      pomodoroPhase === 'work'
                        ? 'text-blue-300/80 bg-blue-500/10'
                        : 'text-emerald-300/80 bg-emerald-500/10'
                    }`}
                  >
                    {pomodoroPhase === 'work' ? 'Fase de Enfoque' : 'Descanso'}
                  </span>
                </div>
              )}
            </div>

            {/* Transport Controls */}
            <div className="relative z-10 flex flex-col items-center gap-2 md:gap-4 mx-auto">
              {/* Play/Pause Button (Glassmorphism Style) */}
              <button
                onClick={handlePlayPause}
                disabled={isLoading}
                className={`
                  relative w-20 h-20 md:w-24 md:h-24 rounded-full
                  flex items-center justify-center
                  backdrop-blur-md
                  border border-white/10
                  transition-all duration-300 ease-out
                  ${isLoading ? 'opacity-50 cursor-wait' : 'hover:scale-110 active:scale-95'}
                `}
                style={{
                  background:
                    isPlaying && !isPaused
                      ? `linear-gradient(135deg, ${modeConfig.color}40, ${modeConfig.color}20)`
                      : 'rgba(255, 255, 255, 0.05)',
                  boxShadow:
                    isPlaying && !isPaused
                      ? `0 0 60px ${modeConfig.color}30, 0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)`
                      : '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
                }}
                aria-label={isPlaying && !isPaused ? 'Pausar' : 'Reproducir'}
              >
                {isLoading ? (
                  <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : isPlaying && !isPaused ? (
                  <Pause className="w-10 h-10 text-white" />
                ) : (
                  <Play className="w-10 h-10 text-white ml-1" />
                )}
              </button>

              {/* Stop Button */}
              <button
                onClick={handleStop}
                disabled={isStopDisabled}
                className={`
                  p-3 rounded-full transition-all duration-200
                  backdrop-blur-sm border border-white/5
                  ${
                    !isStopDisabled
                      ? 'bg-white/5 hover:bg-white/10 text-neutral-300 hover:text-neutral-100'
                      : 'bg-white/[0.02] text-neutral-500 cursor-not-allowed'
                  }
                `}
                title={isPaused ? 'Detener y reiniciar (desde pausa)' : 'Detener'}
                aria-label="Detener"
              >
                <Square className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Footer: Simplified Mixer Section (Anxiety-Friendly) */}
        {/* ================================================================ */}
        <footer className="relative z-10 pb-3 md:pb-4 px-4 md:px-8 mt-auto">
          {!showMixer ? (
            /* ============================================================ */
            /* SIMPLE VIEW: Master Volume Slider + Advanced Settings Button */
            /* ============================================================ */
            <div className="mx-auto max-w-md">
              {/* [v3.4] Voice Guide Toggle - Only visible in Reset (anxiety) mode */}
              {activeModeId === 'anxiety' && (
                <div className="mb-2 p-3 rounded-2xl backdrop-blur-sm border border-white/5 bg-white/[0.02]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isVoiceEnabled ? (
                        <Mic className="w-4 h-4 text-emerald-400/70" />
                      ) : (
                        <MicOff className="w-4 h-4 text-neutral-400" />
                      )}
                      <span className="text-sm text-neutral-400">Voz Guía</span>
                    </div>
                    <button
                      onClick={() => setVoiceEnabled(!isVoiceEnabled)}
                      role="switch"
                      aria-checked={isVoiceEnabled}
                      aria-label="Toggle voice guide"
                      className={`
                        relative w-11 h-6 rounded-full transition-colors duration-300 ease-in-out
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50
                        ${isVoiceEnabled ? 'bg-emerald-500' : 'bg-neutral-600'}
                      `}
                    >
                      <span
                        className={`
                          absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md
                          transition-transform duration-300 ease-in-out
                          ${isVoiceEnabled ? 'translate-x-5' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>
                </div>
              )}

              {/* Master Volume Control */}
              <div className="p-3 md:p-4 rounded-2xl backdrop-blur-sm border border-white/5 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-2 md:mb-3">
                  <div className="flex items-center gap-2">
                    <Volume className="w-4 h-4 text-neutral-400" />
                    <span className="text-sm text-neutral-400">Volumen</span>
                  </div>
                  <span className="text-xs text-neutral-400 font-mono">
                    {Math.round(masterVolume * 100)}%
                  </span>
                </div>

                {/* Horizontal Master Slider */}
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={masterVolume * 100}
                  onChange={(e) => setMasterVolume(Number(e.target.value) / 100)}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer bg-neutral-800"
                  style={{
                    background: `linear-gradient(to right, ${modeConfig.color} 0%, ${modeConfig.color} ${masterVolume * 100}%, #262626 ${masterVolume * 100}%, #262626 100%)`,
                  }}
                />
              </div>

              {/* Advanced Settings Button */}
              <button
                onClick={() => setShowMixer(true)}
                className="w-full mt-2 md:mt-3 py-1.5 md:py-2 px-3 md:px-4 rounded-lg text-[11px] md:text-xs text-neutral-400 hover:text-neutral-300 hover:bg-white/5 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span>Ajustes Avanzados</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          ) : (
            /* ============================================================ */
            /* ADVANCED VIEW: Full 4-Channel Mixer */
            /* ============================================================ */
            <div>
              {/* Mixer Header with Close Button */}
              <div className="flex items-center justify-between mb-3 max-w-2xl mx-auto">
                <div className="flex items-center gap-2">
                  <Waves className="w-4 h-4 text-neutral-400" />
                  <span className="text-xs text-neutral-400 uppercase tracking-wider">
                    Mezclador Avanzado
                  </span>
                </div>
                <button
                  onClick={() => setShowMixer(false)}
                  className="py-1 px-3 rounded text-xs text-neutral-400 hover:text-neutral-300 hover:bg-white/5 transition-all duration-200 flex items-center gap-1"
                >
                  <span>Cerrar</span>
                  <ChevronUp className="w-3 h-3" />
                </button>
              </div>

              {/* Track Faders + Master Fader */}
              <div
                className="flex justify-center gap-6 p-4 rounded-2xl mx-auto max-w-2xl backdrop-blur-sm border border-white/5"
                style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}
              >
                {/* Channel Faders (3 pistas) */}
                <TrackFader
                  trackId="ambience"
                  label="Ambiente"
                  volume={volumes.ambience}
                  onChange={handleVolumeChange}
                  isActive={isPlaying}
                  icon={<Music className="w-4 h-4" />}
                  color={modeConfig.color}
                />

                <TrackFader
                  trackId="tone"
                  label="Binaural"
                  volume={volumes.tone}
                  onChange={handleVolumeChange}
                  isActive={isPlaying}
                  icon={<Waves className="w-4 h-4" />}
                  color={modeConfig.color}
                />

                <TrackFader
                  trackId="voice"
                  label="Voz Guía"
                  volume={volumes.voice}
                  onChange={handleVolumeChange}
                  isActive={isPlaying && currentMode === 'anxiety'}
                  icon={<Mic className="w-4 h-4" />}
                  color={modeConfig.color}
                />

                {/* Separador visual */}
                <div className="w-px h-32 bg-white/10 self-center" style={{ marginTop: '28px' }} />

                {/* Master Fader */}
                <TrackFader
                  trackId="master"
                  label="Máster"
                  volume={masterVolume}
                  onChange={handleVolumeChange}
                  isActive={isPlaying}
                  icon={<Volume className="w-4 h-4" />}
                  color="#FFFFFF"
                />
              </div>
            </div>
          )}

          {/* Science Link - Mobile Only */}
          <div className="md:hidden mt-4 flex justify-center">
            <Link
              to="/science"
              className="flex items-center justify-center gap-2 text-sm text-neutral-400 hover:text-neutral-300 transition-colors py-2 px-4"
            >
              <FlaskConical className="w-4 h-4" />
              <span>Cómo Funciona</span>
            </Link>
          </div>

          {/* Keyboard Hint */}
          <p className="text-center text-xs text-neutral-500 mt-3">
            Espacio: play/pausa • Esc: stop • Usa auriculares para inmersión espacial
          </p>

          {/* Footer Credits */}
          <p className="text-center text-xs text-neutral-500 mt-2">
            {`${APP_NAME} ${APP_VERSION} - ${APP_STUDIO} - ${APP_COUNTRY} ${APP_YEAR}`}
          </p>
        </footer>
      </main>
    </div>
  );
}

export default Player;